import { useMutation, useQuery } from '@tanstack/react-query';
import { useState, type FormEvent, type ReactNode } from 'react';
import { api, ApiError } from '../api/client.js';
import { useI18n } from '../i18n/I18nProvider.js';
import { Alert, Button, TextField } from '../ui/index.js';
import { AuthLayout } from './AuthLayout.js';

function tokenFromUrl(): string {
  return new URLSearchParams(window.location.search).get('token') ?? '';
}

export function ForgotPasswordScreen(): ReactNode {
  const { t } = useI18n();
  const [email, setEmail] = useState('');

  const request = useMutation({
    mutationFn: () => api.post('/api/v1/auth/password/forgot', { email }),
  });

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    request.mutate();
  };

  return (
    <AuthLayout
      title={t('auth.forgot.title')}
      subtitle={t('auth.forgot.subtitle')}
      footer={
        <p>
          <a href="/sign-in">{t('auth.backToSignIn')}</a>
        </p>
      }
    >
      {request.isSuccess ? (
        // Deliberately identical whether or not the address has an account.
        <Alert tone="success">{t('auth.forgot.sent')}</Alert>
      ) : (
        <form className="form" onSubmit={onSubmit} noValidate>
          {request.error === null ? null : (
            <Alert tone="critical">{t('error.SERVICE_UNAVAILABLE')}</Alert>
          )}

          <TextField
            label={t('auth.email')}
            type="email"
            name="email"
            autoComplete="username"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />

          <Button type="submit" variant="primary" loading={request.isPending}>
            {t('auth.forgot.submit')}
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}

export function ResetPasswordScreen(): ReactNode {
  const { t } = useI18n();
  const [token] = useState(tokenFromUrl);
  const [password, setPassword] = useState('');

  const reset = useMutation({
    mutationFn: () => api.post('/api/v1/auth/password/reset', { token, password }),
  });

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    reset.mutate();
  };

  return (
    <AuthLayout title={t('auth.reset.title')}>
      {reset.isSuccess ? (
        <>
          <Alert tone="success">{t('auth.reset.success')}</Alert>
          <p className="form__aside">
            <a href="/sign-in">{t('auth.signIn')}</a>
          </p>
        </>
      ) : (
        <form className="form" onSubmit={onSubmit} noValidate>
          {reset.error === null ? null : (
            <Alert tone="critical">
              {reset.error instanceof ApiError
                ? t(`error.${reset.error.code}`)
                : t('error.SERVICE_UNAVAILABLE')}
            </Alert>
          )}

          <TextField
            label={t('auth.newPassword')}
            type="password"
            name="password"
            autoComplete="new-password"
            required
            minLength={12}
            hint={t('auth.register.passwordHint', { count: 12 })}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />

          <Button type="submit" variant="primary" loading={reset.isPending}>
            {t('auth.reset.submit')}
          </Button>
        </form>
      )}
    </AuthLayout>
  );
}

export function VerifyEmailScreen(): ReactNode {
  const { t } = useI18n();
  const [token] = useState(tokenFromUrl);

  /*
   * A query, not a mutation fired from an effect.
   *
   * The token is single-use, and the effect-plus-ref version spent it twice. A ref guards one
   * mount; the failure is a *remount* — React's strict mode mounts, unmounts and mounts again, and
   * the second mount gets a fresh ref with `false` in it. The first request spent the token, the
   * second was told it was invalid, and the screen the person was looking at was the second one. It
   * said "that verification link is invalid or has expired" to somebody who had just clicked a link
   * that was neither, which is the single worst thing this screen can say.
   *
   * The query cache lives on the client rather than on the component, so a remount finds the
   * request already in flight and joins it instead of starting a second one. Cached for the life
   * of the tab and never retried: retrying a single-use token can only turn a success into a
   * failure.
   */
  const verify = useQuery({
    queryKey: ['verify-email', token],
    queryFn: () => api.post('/api/v1/auth/verify-email', { token }),
    enabled: token !== '',
    retry: false,
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  return (
    <AuthLayout title={t('auth.verify.title')}>
      {verify.isSuccess ? <Alert tone="success">{t('auth.verify.success')}</Alert> : null}
      {verify.isError || token === '' ? (
        <Alert tone="critical">{t('auth.verify.failed')}</Alert>
      ) : null}
      {token !== '' && verify.isPending ? <p>{t('common.loading')}</p> : null}

      <p className="form__aside">
        <a href="/sign-in">{t('auth.backToSignIn')}</a>
      </p>
    </AuthLayout>
  );
}
